import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Paper,
  Typography,
  TextField,
  CircularProgress
} from '@mui/material';
import NotesIcon from '@mui/icons-material/Notes';
import { useUpdateWorkOrder } from '@api/enterprise/workorder/mutations';
import useOnlineStatus from '@hooks/useOnlineStatus';

type Props = {
  jobNumber: string;
  initialNotes: string;
  disabled?: boolean;
};

export const JobNotesSection = ({
  jobNumber,
  initialNotes,
  disabled = false
}: Props) => {
  const { t } = useTranslation('customerReview');
  const isOnline = useOnlineStatus();
  const [notes, setNotes] = useState(initialNotes);
  const [hasChanges, setHasChanges] = useState(false);

  const { mutate: updateWorkOrder, isPending } = useUpdateWorkOrder({
    onSuccess: () => setHasChanges(false),
    showSuccessToast: false
  });

  useEffect(() => {
    setNotes(initialNotes);
  }, [initialNotes]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNotes(e.target.value);
    setHasChanges(true);
  };

  const handleBlur = () => {
    if (hasChanges && notes !== initialNotes && isOnline) {
      updateWorkOrder({
        jobNumber,
        command: { notes }
      });
    }
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Paper
        elevation={1}
        sx={{
          p: { xs: 2, sm: 2, md: 3 },
          borderRadius: 2
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 2
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <NotesIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
            <Typography variant="subtitle1" fontWeight={600}>
              {t('jobNotes.title')}
            </Typography>
          </Box>
          {isPending && <CircularProgress size={20} />}
        </Box>
        <TextField
          fullWidth
          multiline
          minRows={3}
          maxRows={8}
          value={notes}
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder={t('jobNotes.placeholder')}
          disabled={disabled || isPending}
          size="small"
          sx={{
            '& .MuiOutlinedInput-root': {
              backgroundColor: 'background.paper'
            }
          }}
        />
      </Paper>
    </Box>
  );
};
